import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mic, MicOff, X } from "lucide-react";
import { createRecognizer, speak, stopSpeaking } from "@/lib/speech";
import { useI18n } from "@/contexts/I18nContext";
import { trackVoiceUsed } from "@/lib/analytics";

// Intent map: simple keyword matching per language
const INTENTS = [
  { keywords: ["job", "काम", "kaam", "வேலை", "kama"], path: "/jobs" },
  { keywords: ["login", "लॉगिन", "उल्लेख", "உள்நுழை"], path: "/login" },
  { keywords: ["register", "पंजीकरण", "नोंदणी", "பதிவு"], path: "/register" },
  { keywords: ["dashboard", "डैशबोर्ड", "டாஷ்"], path: "/dashboard" },
  { keywords: ["home", "होम", "मुख्य", "வீடு"], path: "/" },
  { keywords: ["contractor", "ठेकेदार", "कंत्राट", "ஒப்பந்த"], path: "/register?role=contractor" },
  { keywords: ["worker", "कामगार", "தொழிலாளி"], path: "/register?role=worker" },
];

export default function VoiceAssistant() {
  const { lang, t } = useI18n();
  const [open, setOpen] = useState(false);
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [reply, setReply] = useState("");
  const recRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => () => { stopSpeaking(); recRef.current?.stop?.(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTranscript = (txt) => {
    setTranscript(txt);
    const low = txt.toLowerCase();
    const hit = INTENTS.find((i) => i.keywords.some((k) => low.includes(k.toLowerCase())));
    if (hit) {
      const msg = t("brand") + " → " + hit.path;
      setReply(msg);
      speak(msg, lang);
      setTimeout(() => nav(hit.path), 700);
    } else {
      const msg = lang === "hi" ? "मैंने नहीं समझा, कृपया दोबारा बोलें" :
                  lang === "mr" ? "मला समजले नाही, कृपया पुन्हा बोला" :
                  lang === "ta" ? "எனக்குப் புரியவில்லை, மீண்டும் சொல்லுங்கள்" :
                  "I didn't understand. Please try again.";
      setReply(msg);
      speak(msg, lang);
    }
  };

  const toggleListen = () => {
    if (listening) { recRef.current?.stop?.(); setListening(false); return; }
    const rec = createRecognizer(lang, {
      onResult: handleTranscript,
      onEnd: () => setListening(false),
    });
    if (!rec) {
      setReply("Voice not supported on this browser.");
      speak("Voice not supported on this browser.", "en");
      return;
    }
    recRef.current = rec;
    setTranscript(""); setReply("");
    rec.start();
    setListening(true);
  };

  return (
    <>
      {!open && (
        <button
          data-testid="voice-fab"
          onClick={() => { setOpen(true); trackVoiceUsed(lang); }}
          className={`fixed bottom-6 right-6 z-50 flex h-16 w-16 items-center justify-center rounded-full bg-sarthi-primary text-white shadow-[0_6px_0_0_#B84A25] transition-transform hover:scale-105 active:scale-95 ${listening ? "pulse-ring" : ""}`}
          title={t("voice_help")}
        >
          <Mic className="h-7 w-7" strokeWidth={2.5} />
        </button>
      )}
      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[min(360px,calc(100vw-2rem))] rounded-2xl border-2 border-sarthi-border bg-white p-4 shadow-[0_8px_0_0_rgba(0,0,0,0.08)]">
          <div className="mb-2 flex items-center justify-between">
            <div className="font-display text-lg font-extrabold text-sarthi-text">{t("voice_help")}</div>
            <button data-testid="voice-close" onClick={() => { setOpen(false); recRef.current?.stop?.(); }} className="rounded-lg p-1 hover:bg-sarthi-accent">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="mb-3 min-h-[52px] rounded-xl border-2 border-dashed border-sarthi-border bg-sarthi-accent/60 p-3 text-sm text-sarthi-text">
            {transcript || t("voice_help") + "…"}
          </div>
          {reply && <div className="mb-3 text-sm font-semibold text-sarthi-secondary">{reply}</div>}
          <button
            data-testid="voice-listen-btn"
            onClick={toggleListen}
            className={`flex w-full items-center justify-center gap-2 rounded-xl py-3 font-bold text-white ${listening ? "bg-red-600" : "bg-sarthi-primary"} ${listening ? "pulse-ring" : ""}`}
          >
            {listening ? <><MicOff className="h-5 w-5" /> Stop</> : <><Mic className="h-5 w-5" /> Start</>}
          </button>
          <div className="mt-2 text-center text-xs text-sarthi-muted">{"Try: 'jobs', 'login', 'काम', 'पंजीकरण'"}</div>
        </div>
      )}
    </>
  );
}
